<script setup lang="ts">
import { computed, ref } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  fetchTimesheetsCsv,
  generateTimesheets,
  getRoster,
  listCostReport,
  listRosters,
  listTimesheets,
  setJobRate,
  type RosterFinding,
} from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import WorkerAvatar from "@/components/WorkerAvatar.vue";

const LAYER_LABEL: Record<string, string> = {
  minimum_core: "Minimum-core",
  sentinel: "Sentinel",
  prerequisites: "Pre-req",
};
function findingDetail(f: RosterFinding): string {
  switch (f.reason) {
    case "missing": return "missing";
    case "below_level": return `below level (need ${f.required}, has ${f.held ?? "?"})`;
    case "expired": return "expired";
    case "expiring": return `expiring${f.days_left != null ? ` in ${f.days_left}d` : ""}`;
    case "level_unknown": return `level not recorded (need ${f.required})`;
    case "not_tracked": return "not tracked yet";
    default: return f.reason;
  }
}

const queryClient = useQueryClient();

const { data: timesheets, isPending, isError, error } = useQuery({
  queryKey: ["timesheets"],
  queryFn: () => listTimesheets(),
});
const { data: costReport } = useQuery({
  queryKey: ["timesheet-cost-report"],
  queryFn: () => listCostReport(),
});
const { data: rosters } = useQuery({
  queryKey: ["rosters"],
  queryFn: listRosters,
});

// Payroll/accounts CSV export.
const csvBusy = ref(false);
async function exportCsv(): Promise<void> {
  csvBusy.value = true;
  try {
    const blob = await fetchTimesheetsCsv();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "timesheets.csv";
    a.click();
    URL.revokeObjectURL(url);
  } finally {
    csvBusy.value = false;
  }
}

// Editable charge rate per job code (keyed by code).
const rateDraft = ref<Record<string, string>>({});
const savingRate = ref<string | null>(null);

async function saveRate(jobCode: string | null): Promise<void> {
  if (!jobCode) return;
  const raw = rateDraft.value[jobCode];
  const rate = Number(raw);
  if (raw === undefined || raw === "" || Number.isNaN(rate) || rate < 0) return;
  savingRate.value = jobCode;
  try {
    await setJobRate(jobCode, rate);
    await queryClient.invalidateQueries({ queryKey: ["timesheet-cost-report"] });
  } finally {
    savingRate.value = null;
  }
}

function gbp(value: number | null): string {
  return value === null
    ? "—"
    : value.toLocaleString("en-GB", { style: "currency", currency: "GBP" });
}

/** Signed currency for a variance (+over / −under budget). */
function variance(value: number | null): string {
  if (value === null) return "—";
  if (value === 0) return gbp(0);
  return (value > 0 ? "+" : "−") + gbp(Math.abs(value));
}

/** Over budget is red, under is green. */
function varianceClass(value: number | null): string {
  if (value === null || value === 0) return "text-neutral-500";
  return value > 0 ? "text-semantic-danger" : "text-semantic-success";
}

const totals = computed(() => {
  const rows = costReport.value ?? [];
  const sum = (pick: (r: (typeof rows)[number]) => number | null): number =>
    rows.reduce((s, r) => s + (pick(r) ?? 0), 0);
  return {
    plannedCost: sum((r) => r.planned_cost),
    cost: sum((r) => r.cost),
    varianceCost: sum((r) => r.variance_cost),
    confirmedCost: sum((r) => r.confirmed_cost),
    anyRate: rows.some((r) => r.rate !== null),
  };
});

const selectedRoster = ref("");

// Pre-deployment competency check for the selected roster week.
const { data: rosterCheck, isFetching: checkLoading } = useQuery({
  queryKey: ["roster-check", selectedRoster],
  queryFn: () => getRoster(selectedRoster.value),
  enabled: computed(() => !!selectedRoster.value),
});
const flagged = computed(() =>
  (rosterCheck.value?.assignments ?? []).filter(
    (a) => a.rosterable === false || a.worker_found === false,
  ),
);

const generating = ref(false);
const genMessage = ref<string | null>(null);
const genError = ref<string | null>(null);

async function generate(): Promise<void> {
  if (!selectedRoster.value) return;
  generating.value = true;
  genMessage.value = null;
  genError.value = null;
  try {
    const result = await generateTimesheets(selectedRoster.value);
    genMessage.value = `Generated ${result.generated} timesheets for ${result.rail_week}.`;
    await queryClient.invalidateQueries({ queryKey: ["timesheets"] });
    await queryClient.invalidateQueries({ queryKey: ["timesheet-cost-report"] });
  } catch (e) {
    genError.value = e instanceof Error ? e.message : String(e);
  } finally {
    generating.value = false;
  }
}

function hrs(value: number | null): string {
  return value === null ? "—" : `${value}h`;
}
</script>

<template>
  <PageLayout wide>
    <div class="grid gap-8">
      <header class="flex items-start justify-between gap-4">
        <div>
          <h1 class="text-2xl font-extrabold text-neutral-900">
            Timesheets
          </h1>
          <p class="mt-1 text-neutral-500">
            Weekly timesheets — planned hours from the roster, reconciled against
            the diary sign-ons, ready to adjust and approve.
          </p>
        </div>
        <button
          type="button"
          class="shrink-0 rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
          :disabled="csvBusy"
          title="Per-worker hours + pay basis (payment type) + CIS UTR, for payroll"
          @click="exportCsv"
        >
          {{ csvBusy ? "Exporting…" : "Export CSV (payroll)" }}
        </button>
      </header>

      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-4 font-semibold">
          Generate for a week
        </h2>
        <div
          v-if="!rosters?.length"
          class="text-sm text-neutral-500"
        >
          No rosters imported yet — import the roster workbook on the Workers
          page first.
        </div>
        <div
          v-else
          class="flex flex-wrap items-end gap-4"
        >
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Roster week</span>
            <select
              v-model="selectedRoster"
              class="rounded-md border border-neutral-300 px-3 py-2"
            >
              <option value="">
                Select…
              </option>
              <option
                v-for="r in rosters"
                :key="r.id"
                :value="r.id"
              >
                {{ r.rail_week }} ({{ r.period_start }} to {{ r.period_end }})
              </option>
            </select>
          </label>
          <button
            type="button"
            class="rounded-md bg-brand-primary px-4 py-2 font-semibold text-brand-primary-on transition hover:opacity-90 disabled:opacity-50"
            :disabled="generating || !selectedRoster"
            @click="generate"
          >
            {{ generating ? "Generating…" : "Generate timesheets" }}
          </button>
        </div>

        <!-- Pre-deployment competency check over the rostered gang -->
        <div
          v-if="selectedRoster && rosterCheck?.rostering_summary"
          class="mt-4 rounded-lg border border-neutral-200 p-4"
        >
          <div class="flex flex-wrap items-center gap-3">
            <h3 class="text-sm font-semibold text-neutral-900">
              Rostering check
            </h3>
            <span
              v-if="checkLoading"
              class="text-xs text-neutral-400"
            >Checking…</span>
            <span
              class="rounded-full bg-semantic-success/10 px-2 py-0.5 text-xs text-semantic-success"
            >{{ rosterCheck.rostering_summary.rosterable }} rosterable</span>
            <span
              v-if="rosterCheck.rostering_summary.not_rosterable"
              class="rounded-full bg-semantic-danger/10 px-2 py-0.5 text-xs font-medium text-semantic-danger"
            >{{ rosterCheck.rostering_summary.not_rosterable }} not rosterable</span>
            <span
              v-if="rosterCheck.rostering_summary.no_worker"
              class="rounded-full bg-semantic-warning/10 px-2 py-0.5 text-xs text-semantic-warning"
            >{{ rosterCheck.rostering_summary.no_worker }} not on worker master</span>
            <span
              v-if="rosterCheck.rostering_summary.no_role"
              class="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500"
            >{{ rosterCheck.rostering_summary.no_role }} no role set</span>
          </div>

          <p
            v-if="!flagged.length"
            class="mt-2 text-sm text-neutral-500"
          >
            Everyone rostered this week is cleared for their role.
          </p>
          <ul
            v-else
            class="mt-2 grid gap-1.5"
          >
            <li
              v-for="a in flagged"
              :key="a.assignment_id"
              class="border-t border-neutral-50 pt-1.5 first:border-0 first:pt-0 text-sm"
            >
              <div class="flex flex-wrap items-center gap-2">
                <WorkerAvatar
                  :name="a.name_as_rostered || a.ncca_no"
                  :file-id="a.image_file_id"
                  :size="26"
                />
                <RouterLink
                  v-if="a.worker_id"
                  :to="`/workers/${a.worker_id}`"
                  class="font-medium text-brand-primary hover:underline"
                >
                  {{ a.name_as_rostered || a.ncca_no }}
                </RouterLink>
                <span
                  v-else
                  class="font-medium text-neutral-900"
                >{{ a.name_as_rostered || a.ncca_no }}</span>
                <span class="text-xs text-neutral-400">{{ a.worker_role || a.skill || "—" }}</span>
                <span
                  v-if="a.worker_found === false"
                  class="rounded-full bg-semantic-warning/10 px-2 py-0.5 text-xs text-semantic-warning"
                >No worker-master record (NCCA {{ a.ncca_no }})</span>
                <span
                  v-else
                  class="rounded-full bg-semantic-danger/10 px-2 py-0.5 text-xs text-semantic-danger"
                >Not rosterable</span>
              </div>
              <div
                v-if="a.blockers && a.blockers.length"
                class="mt-0.5 flex flex-wrap gap-1 pl-1"
              >
                <span
                  v-for="(b, i) in a.blockers"
                  :key="i"
                  class="text-xs text-neutral-500"
                >
                  <span class="rounded bg-neutral-100 px-1">{{ LAYER_LABEL[b.layer] }}</span>
                  {{ b.competency }} <span class="text-semantic-danger">{{ findingDetail(b) }}</span>
                </span>
              </div>
            </li>
          </ul>
        </div>
        <p
          v-if="genMessage"
          class="mt-3 text-sm text-semantic-success"
        >
          {{ genMessage }}
        </p>
        <p
          v-if="genError"
          class="mt-3 text-sm text-semantic-danger"
        >
          {{ genError }}
        </p>
      </section>

      <section
        v-if="costReport?.length"
        class="rounded-xl border border-neutral-200 bg-neutral-0 p-6"
      >
        <h2 class="mb-1 font-semibold">
          Cost by job code
        </h2>
        <p class="mb-4 text-sm text-neutral-500">
          Planned (roster) vs actual hours — actual is reconciled from the diary
          sign-ons, or the adjusted / planned figure where there are none —
          costed at the charge rate you set per invoicing code.
        </p>
        <div class="overflow-x-auto">
          <table class="w-full text-left text-sm">
            <thead class="text-neutral-500">
              <tr>
                <th class="py-1 pr-4 font-medium">
                  Job code
                </th>
                <th class="py-1 pr-4 font-medium">
                  Workers
                </th>
                <th class="py-1 pr-4 font-medium">
                  Planned h
                </th>
                <th class="py-1 pr-4 font-medium">
                  Actual h
                </th>
                <th class="py-1 pr-4 font-medium">
                  Rate (£/h)
                </th>
                <th class="py-1 pr-4 text-right font-medium">
                  Planned
                </th>
                <th class="py-1 pr-4 text-right font-medium">
                  Actual
                </th>
                <th class="py-1 text-right font-medium">
                  Variance
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="j in costReport"
                :key="j.job_code ?? 'none'"
                class="border-t border-neutral-100"
              >
                <td class="py-1.5 pr-4 font-medium text-neutral-900">
                  {{ j.job_code ?? "— (no code)" }}
                </td>
                <td class="py-1.5 pr-4 text-neutral-500">
                  {{ j.worker_count }}
                </td>
                <td class="py-1.5 pr-4 text-neutral-500">
                  {{ hrs(j.planned_hours) }}
                </td>
                <td class="py-1.5 pr-4 text-neutral-900">
                  {{ hrs(j.actual_hours) }}
                </td>
                <td class="py-1.5 pr-4">
                  <input
                    v-if="j.job_code"
                    type="number"
                    min="0"
                    step="0.5"
                    :value="rateDraft[j.job_code] ?? (j.rate ?? '')"
                    :disabled="savingRate === j.job_code"
                    class="w-20 rounded-md border border-neutral-300 px-2 py-1 text-sm"
                    @input="rateDraft[j.job_code] = ($event.target as HTMLInputElement).value"
                    @keyup.enter="saveRate(j.job_code)"
                    @blur="saveRate(j.job_code)"
                  >
                  <span
                    v-else
                    class="text-neutral-400"
                  >—</span>
                </td>
                <td class="py-1.5 pr-4 text-right text-neutral-500">
                  {{ gbp(j.planned_cost) }}
                </td>
                <td class="py-1.5 pr-4 text-right font-semibold text-neutral-900">
                  {{ gbp(j.cost) }}
                </td>
                <td
                  class="py-1.5 text-right font-medium"
                  :class="varianceClass(j.variance_cost)"
                >
                  {{ variance(j.variance_cost) }}
                </td>
              </tr>
              <tr class="border-t border-neutral-200 font-semibold">
                <td class="py-2 pr-4 text-neutral-500">
                  Total
                </td>
                <td colspan="4" />
                <td class="py-2 pr-4 text-right text-neutral-500">
                  {{ gbp(totals.plannedCost) }}
                </td>
                <td class="py-2 pr-4 text-right text-neutral-900">
                  {{ gbp(totals.cost) }}
                </td>
                <td
                  class="py-2 text-right"
                  :class="varianceClass(totals.varianceCost)"
                >
                  {{ variance(totals.varianceCost) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p
          v-if="totals.anyRate"
          class="mt-3 text-sm text-neutral-500"
        >
          Of the actual cost, <span class="font-semibold text-neutral-900">{{ gbp(totals.confirmedCost) }}</span>
          is confirmed (from approved timesheets); the rest is provisional until approved.
        </p>
      </section>

      <div
        v-if="isPending"
        class="text-neutral-500"
      >
        Loading…
      </div>
      <div
        v-else-if="isError"
        class="rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
      >
        Failed to load: {{ error?.message }}
      </div>
      <div
        v-else
        class="overflow-hidden rounded-xl border border-neutral-200"
      >
        <table class="w-full text-left text-sm">
          <thead class="bg-neutral-50 text-neutral-500">
            <tr>
              <th class="px-4 py-2 font-medium">
                Worker
              </th>
              <th class="px-4 py-2 font-medium">
                Week
              </th>
              <th class="px-4 py-2 font-medium">
                Job code
              </th>
              <th class="px-4 py-2 font-medium">
                Planned
              </th>
              <th class="px-4 py-2 font-medium">
                PO
              </th>
              <th class="px-4 py-2 font-medium">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="t in timesheets"
              :key="t.id"
              class="cursor-pointer border-t border-neutral-100 hover:bg-neutral-50"
              @click="$router.push(`/timesheets/${t.id}`)"
            >
              <td class="px-4 py-2 font-medium text-neutral-900">
                {{ t.worker_name }}
              </td>
              <td class="px-4 py-2 text-neutral-500">
                {{ t.rail_week }}
              </td>
              <td class="px-4 py-2 text-neutral-500">
                {{ t.job_code ?? "—" }}
              </td>
              <td class="px-4 py-2 text-neutral-500">
                {{ hrs(t.planned_hours) }}
              </td>
              <td class="px-4 py-2 text-neutral-500">
                {{ t.po_number ?? "—" }}
              </td>
              <td class="px-4 py-2">
                <span
                  :class="t.status === 'approved' ? 'text-semantic-success' : 'text-neutral-400'"
                >
                  {{ t.status }}
                </span>
              </td>
            </tr>
            <tr v-if="!timesheets?.length">
              <td
                colspan="6"
                class="px-4 py-6 text-center text-neutral-400"
              >
                No timesheets — generate them from an imported roster week.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </PageLayout>
</template>
